import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Star, Moon, Users, Sparkles, Shield, TrendingUp, Activity, CheckCircle, AlertTriangle, FileText, ExternalLink } from 'lucide-react';
import KundliChart from '../../free-kundli/components/KundliChart';
import { useKundliStore } from '@store/kundliStore';

interface CompatibilityResultsProps {
    results: any;
    language: 'english' | 'hindi';
    translations: any;
    person1FormData: any;
    person2FormData: any;
}

const CompatibilityResults: React.FC<CompatibilityResultsProps> = ({ results, language, translations: t, person1FormData, person2FormData }) => {
    const navigate = useNavigate();
    const { setCurrentKundliData, setCurrentFormData, setCurrentSelectedChart } = useKundliStore();

    const { compatibility_data, person1, person2 } = results;
    const gunaMilan = compatibility_data?.guna_milan;
    const charts = compatibility_data?.charts || [];
    const summary = compatibility_data?.summary;

    // Handle View Full Kundli button click
    const handleViewFullKundli = (formDataFromProp: any) => {
        // Use the form data directly - it has all the required fields
        const formData = {
            name: formDataFromProp.name || '',
            dob: formDataFromProp.dob || '',
            birth_time: formDataFromProp.birth_time || '12:00',
            location: formDataFromProp.location || '',
            latitude: formDataFromProp.latitude || 0,
            longitude: formDataFromProp.longitude || 0,
            timezone: '+5.5',
            gender: formDataFromProp.gender || 'Male',
            marital_status: 'Single',
            occupation: 'Professional'
        };

        // Set only form data - clear kundli data so FreeKundli will fetch fresh data
        setCurrentKundliData(null);
        setCurrentFormData(formData);
        setCurrentSelectedChart('D1');

        // Navigate to free kundli page with auto-fetch flag
        navigate(`/free-kundli/${language}?autoGenerate=true`);
    };

    // Get language-specific summary
    const getSummaryText = () => {
        if (!summary) return null;
        const langKey = language === 'hindi' ? 'hindi' : 'english';
        return summary[langKey] || summary;
    };
    const summaryData = getSummaryText();

    // Map koota keys to translation name keys
    const getKootaTranslatedName = (koota: string) => {
        const kootaNameMap: Record<string, string> = {
            'Varna': 'varnaName',
            'Vashya': 'vashyaName',
            'Tara': 'taraName',
            'Yoni': 'yoniName',
            'GrahaMaitri': 'grahaMaitriName',
            'Gana': 'ganaName',
            'Bhakoot': 'bhakootName',
            'Nadi': 'nadiName'
        };
        const key = kootaNameMap[koota];
        return key ? t[key] : koota;
    };

    const totalScore = gunaMilan?.total || 0;
    const maxScore = gunaMilan?.max_total || 36;
    const percentage = parseFloat(((totalScore / maxScore) * 100).toFixed(1));

    const getCompatibilityLevel = () => {
        if (percentage >= 80) return { label: t.excellent, color: 'text-green-400', bg: 'bg-green-500' };
        if (percentage >= 60) return { label: t.good, color: 'text-blue-400', bg: 'bg-blue-500' };
        if (percentage >= 40) return { label: t.average, color: 'text-yellow-400', bg: 'bg-yellow-500' };
        if (percentage >= 20) return { label: t.belowAverage, color: 'text-orange-400', bg: 'bg-orange-500' };
        return { label: t.low, color: 'text-red-400', bg: 'bg-red-500' };
    };

    const compatLevel = getCompatibilityLevel();

    const kootaInfo: Record<string, { icon: any; descKey: string; nameKey: string }> = {
        Varna: { icon: Users, descKey: 'varnaDesc', nameKey: 'varnaName' },
        Vashya: { icon: Heart, descKey: 'vashyaDesc', nameKey: 'vashyaName' },
        Tara: { icon: Star, descKey: 'taraDesc', nameKey: 'taraName' },
        Yoni: { icon: Activity, descKey: 'yoniDesc', nameKey: 'yoniName' },
        GrahaMaitri: { icon: Moon, descKey: 'grahaMaitriDesc', nameKey: 'grahaMaitriName' },
        Gana: { icon: Sparkles, descKey: 'ganaDesc', nameKey: 'ganaName' },
        Bhakoot: { icon: TrendingUp, descKey: 'bhakootDesc', nameKey: 'bhakootName' },
        Nadi: { icon: Shield, descKey: 'nadiDesc', nameKey: 'nadiName' }
    };

    return (
        <div className="space-y-8">
            {/* Overall Score Card */}
            <div className="bg-gradient-to-br from-pink-900/30 to-purple-900/30 backdrop-blur-xl border border-white/20 rounded-[24px] md:rounded-[32px] p-6 md:p-10 text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-64 h-64 bg-pink-500/10 blur-[100px] rounded-full -ml-32 -mt-32" />
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500/10 blur-[100px] rounded-full -mr-32 -mb-32" />

                <div className="relative z-10">
                    {/* Names */}
                    <div className="flex items-center justify-center gap-4 md:gap-8 mb-8">
                        <div className="text-center">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-2 shadow-lg shadow-blue-500/30">
                                <span className="text-2xl md:text-3xl font-bold text-white">{person1?.name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                            <p className="text-white font-bold text-sm md:text-base">{person1?.name}</p>
                            <p className="text-white/50 text-xs">{charts[0]?.rashi}</p>
                        </div>

                        <div className="flex flex-col items-center">
                            <Heart className="w-8 h-8 md:w-10 md:h-10 text-pink-500 fill-pink-500 animate-pulse" />
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mx-auto mb-2 shadow-lg shadow-pink-500/30">
                                <span className="text-2xl md:text-3xl font-bold text-white">{person2?.name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                            <p className="text-white font-bold text-sm md:text-base">{person2?.name}</p>
                            <p className="text-white/50 text-xs">{charts[1]?.rashi}</p>
                        </div>
                    </div>

                    {/* Score Circle */}
                    <div className="relative w-40 h-40 md:w-48 md:h-48 mx-auto mb-6">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                            <circle
                                cx="50"
                                cy="50"
                                r="45"
                                fill="none"
                                stroke="rgba(255,255,255,0.1)"
                                strokeWidth="8"
                            />
                            <circle
                                cx="50"
                                cy="50"
                                r="45"
                                fill="none"
                                stroke="url(#gradient)"
                                strokeWidth="8"
                                strokeLinecap="round"
                                strokeDasharray={`${(percentage / 100) * 283} 283`}
                            />
                            <defs>
                                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#ec4899" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-4xl md:text-5xl font-black text-white">{totalScore}</span>
                            <span className="text-sm text-white/60">{t.outOf} {maxScore}</span>
                        </div>
                    </div>

                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${compatLevel.bg}/20 border border-white/10`}>
                        <div className={`w-2 h-2 rounded-full ${compatLevel.bg}`} />
                        <span className={`font-bold ${compatLevel.color}`}>{compatLevel.label} {t.compatibility}</span>
                        <span className="text-white/60">({percentage}%)</span>
                    </div>
                </div>
            </div>

            {/* Compatibility Summary */}
            {summaryData && (
                <div className="bg-gradient-to-br from-indigo-900/20 to-purple-900/20 backdrop-blur-xl border border-white/10 rounded-[24px] md:rounded-[32px] p-6 md:p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <FileText className="text-indigo-400" />
                        {t.compatibilitySummary}
                    </h3>

                    {/* Summary Text */}
                    <p className="text-white/80 leading-relaxed text-base md:text-lg mb-6">
                        {summaryData.summary}
                    </p>

                    {/* Strengths and Concerns Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Strengths */}
                        {summary?.strengths && summary.strengths.length > 0 && (
                            <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <CheckCircle size={20} className="text-green-400" />
                                    <h4 className="font-bold text-green-400">{t.strengths}</h4>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {summary.strengths.map((strength: string, idx: number) => (
                                        <span
                                            key={idx}
                                            className="px-3 py-1.5 bg-green-500/20 text-green-300 rounded-lg text-sm font-medium"
                                        >
                                            {getKootaTranslatedName(strength)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Concerns */}
                        {summary?.concerns && summary.concerns.length > 0 && (
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle size={20} className="text-amber-400" />
                                    <h4 className="font-bold text-amber-400">{t.areasOfConcern}</h4>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {summary.concerns.map((concern: string, idx: number) => (
                                        <span
                                            key={idx}
                                            className="px-3 py-1.5 bg-amber-500/20 text-amber-300 rounded-lg text-sm font-medium"
                                        >
                                            {getKootaTranslatedName(concern)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Koota Analysis Grid */}
            <div className="bg-white/[0.05] backdrop-blur-xl border border-white/10 rounded-[24px] md:rounded-[32px] p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <Star className="text-yellow-400" />
                    {t.kootaAnalysis}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(kootaInfo).map(([key, info]) => {
                        const koota = gunaMilan?.[key];
                        if (!koota) return null;

                        const kootaPercentage = Math.round((koota.points / koota.max) * 100);
                        const IconComponent = info.icon;

                        return (
                            <div
                                key={key}
                                className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 hover:bg-white/[0.05] transition-colors"
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
                                            <IconComponent size={18} className="text-primary-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white">{t[info.nameKey]}</h4>
                                            <p className="text-xs text-white/50">
                                                {t[info.descKey]}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-lg font-bold text-white">{koota.points}</span>
                                        <span className="text-white/40">/{koota.max}</span>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${
                                            kootaPercentage >= 75 ? 'bg-green-500' :
                                            kootaPercentage >= 50 ? 'bg-blue-500' :
                                            kootaPercentage >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${kootaPercentage}%` }}
                                    />
                                </div>

                                {/* Details */}
                                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                    {key === 'Varna' && koota.varna1 && (
                                        <>
                                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg">{koota.varna1}</span>
                                            <span className="text-white/30">+</span>
                                            <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded-lg">{koota.varna2}</span>
                                        </>
                                    )}
                                    {key === 'Vashya' && koota.class1 && (
                                        <>
                                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg">{koota.class1}</span>
                                            <span className="text-white/30">+</span>
                                            <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded-lg">{koota.class2}</span>
                                        </>
                                    )}
                                    {key === 'Tara' && (
                                        <>
                                            {/* Show bidirectional Tara if available */}
                                            {koota.tara_name_boy_to_girl ? (
                                                <>
                                                    <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg">
                                                        {t.boyToGirl}: {koota.tara_name_boy_to_girl}
                                                    </span>
                                                    <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded-lg">
                                                        {t.girlToBoy}: {koota.tara_name_girl_to_boy}
                                                    </span>
                                                </>
                                            ) : koota.tara_name && (
                                                <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-lg">{koota.tara_name}</span>
                                            )}
                                        </>
                                    )}
                                    {key === 'Yoni' && koota.yoni1 && (
                                        <>
                                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg">{koota.yoni1}</span>
                                            <span className="text-white/30">+</span>
                                            <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded-lg">{koota.yoni2}</span>
                                        </>
                                    )}
                                    {key === 'GrahaMaitri' && koota.relationship && (
                                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded-lg">{koota.relationship}</span>
                                    )}
                                    {key === 'Gana' && koota.gana1 && (
                                        <>
                                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg">{koota.gana1}</span>
                                            <span className="text-white/30">+</span>
                                            <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded-lg">{koota.gana2}</span>
                                        </>
                                    )}
                                    {key === 'Bhakoot' && (
                                        <>
                                            {/* Show house position */}
                                            {koota.house_position && (
                                                <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-lg">
                                                    {t.housePosition}: {koota.house_position}
                                                </span>
                                            )}
                                            {/* Show dosha or cancellation */}
                                            {koota.cancellation ? (
                                                <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-lg">
                                                    {t.cancellation}: {koota.cancellation}
                                                </span>
                                            ) : koota.dosha && (
                                                <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded-lg">
                                                    {koota.dosha}
                                                </span>
                                            )}
                                        </>
                                    )}
                                    {key === 'Nadi' && koota.nadi1 && (
                                        <>
                                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg">{koota.nadi1}</span>
                                            <span className="text-white/30">+</span>
                                            <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded-lg">{koota.nadi2}</span>
                                            {/* Show Nadi dosha or cancellation */}
                                            {koota.cancellation ? (
                                                <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-lg">
                                                    {t.cancellation}: {koota.cancellation}
                                                </span>
                                            ) : koota.nadi_dosha && (
                                                <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded-lg">
                                                    {t.nadiDosha}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Birth Chart Details with Lagna Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {charts.map((chart: any, index: number) => {
                    const personFormData = index === 0 ? person1FormData : person2FormData;
                    return (
                        <div
                            key={index}
                            className="bg-white/[0.05] backdrop-blur-xl border border-white/10 rounded-[24px] p-6"
                        >
                            {/* Header */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className={`w-12 h-12 rounded-xl ${index === 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-pink-500 to-pink-600'} flex items-center justify-center`}>
                                    <span className="text-xl font-bold text-white">{chart.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                                <div>
                                    <h4 className="font-bold text-white">{chart.name}</h4>
                                    <p className="text-sm text-white/50">{t.birthDetails}</p>
                                </div>
                            </div>

                            {/* Lagna Chart */}
                            {chart.d1?.planets && (
                                <div className="mb-6">
                                    <h5 className="text-sm font-bold text-white/70 mb-3 flex items-center gap-2">
                                        <Star size={14} className="text-yellow-400" />
                                        {t.lagnaChart}
                                    </h5>
                                    <div className="bg-white/[0.03] rounded-xl p-2">
                                        <KundliChart
                                            planets={chart.d1.planets}
                                            language={language}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Birth Details Grid */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="bg-white/[0.03] rounded-xl p-4">
                                    <p className="text-xs text-white/50 mb-1">{t.rashi}</p>
                                    <p className="font-bold text-white">{chart.rashi}</p>
                                </div>
                                <div className="bg-white/[0.03] rounded-xl p-4">
                                    <p className="text-xs text-white/50 mb-1">{t.nakshatra}</p>
                                    <p className="font-bold text-white">{chart.nakshatra}</p>
                                </div>
                                <div className="bg-white/[0.03] rounded-xl p-4">
                                    <p className="text-xs text-white/50 mb-1">{t.pada}</p>
                                    <p className="font-bold text-white">{chart.pada}</p>
                                </div>
                                <div className="bg-white/[0.03] rounded-xl p-4">
                                    <p className="text-xs text-white/50 mb-1">{t.signLord}</p>
                                    <p className="font-bold text-white">{chart.sign_lord}</p>
                                </div>
                            </div>

                            {/* View Full Kundli Button */}
                            <button
                                onClick={() => handleViewFullKundli(personFormData)}
                                className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                                    index === 0
                                        ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30'
                                        : 'bg-pink-500/20 text-pink-300 hover:bg-pink-500/30 border border-pink-500/30'
                                }`}
                            >
                                <ExternalLink size={16} />
                                {t.viewFullKundli}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CompatibilityResults;
